import { PlayArrow } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useMemo } from 'react';

import { CurrencyDropDown, InvoiceExpenseType } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { baseHeader } from 'styles/components/Common';
import {
  simulatorInputSectionStyle,
  simulatorInputsRowStyle,
  simulatorInputWrapperStyle,
  simulatorAmountWrapperStyle,
  simulatorCurrencyWrapperStyle,
  simulatorSimulateButtonStyle,
  simulatorSecondInputsRowStyle,
  simulatorLabelStyle
} from 'styles/pages/ApprovalRuleSimulatorStyles';

import { SimulatorFormValues } from '../../../../utils/interfaces/ApprovalSimulator';

export interface ApprovalRuleSimulatorFormProps {
  expenseTypes?: InvoiceExpenseType[];
  counterparties?: Array<{ id: number; name: string }>;
  currencies?: CurrencyDropDown[];
  defaultCurrency?: CurrencyDropDown | undefined;
  onSubmit: (values: SimulatorFormValues) => void;
  onChange: () => void;
}

export const ApprovalRuleSimulatorForm = ({
  expenseTypes,
  counterparties,
  currencies,
  defaultCurrency,
  onSubmit,
  onChange
}: ApprovalRuleSimulatorFormProps) => {
  const { translate } = useTranslations();

  const formInitialValues = useMemo(
    () => ({
      expenseType: undefined,
      amount: undefined,
      currency: defaultCurrency,
      counterparty: undefined,
      notValidatedCounterparty: false
    }),
    [defaultCurrency]
  );

  const defaultCurrencyIsoCode = defaultCurrency?.isoCode || 'EUR';

  return (
    <Formik<SimulatorFormValues>
      initialValues={formInitialValues}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values }) => {
        const isFormComplete = !!(values.amount && values.amount > 0);

        return (
          <Form>
            <Box sx={simulatorInputSectionStyle}>
              <Typography sx={baseHeader}>
                {translate('approvalSimulator.sampleInputs')}
              </Typography>

              <Box sx={simulatorInputsRowStyle}>
                <Box sx={simulatorInputWrapperStyle}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label={translate('approvalSimulator.expenseType')}
                      select
                      value={values.expenseType?.id || ''}
                      onChange={(e) => {
                        onChange();
                        const selected = expenseTypes?.find(
                          (et) => et.id === Number(e.target.value)
                        );
                        setFieldValue('expenseType', selected);
                      }}
                      size="small"
                    >
                      {expenseTypes?.map((expenseType) => (
                        <MenuItem key={expenseType.id} value={expenseType.id}>
                          {expenseType.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Box>

                <Box sx={simulatorInputWrapperStyle}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label={translate('approvalSimulator.counterparty')}
                      select
                      value={values.counterparty?.id || ''}
                      onChange={(e) => {
                        onChange();
                        const selected = counterparties?.find(
                          (cp) => cp.id === Number(e.target.value)
                        );
                        setFieldValue('counterparty', selected);
                      }}
                      size="small"
                    >
                      {counterparties?.map((counterparty) => (
                        <MenuItem key={counterparty.id} value={counterparty.id}>
                          {counterparty.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Box>
              </Box>

              <Box sx={simulatorSecondInputsRowStyle}>
                <Box sx={simulatorAmountWrapperStyle}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label={translate('approvalSimulator.amount')}
                      type="number"
                      value={values.amount || ''}
                      onChange={(e) => {
                        onChange();
                        const value = e.target.value
                          ? Number.parseFloat(e.target.value)
                          : undefined;
                        setFieldValue('amount', value);
                      }}
                      size="small"
                      required
                      inputProps={{
                        min: 0
                      }}
                    />
                  </FormControl>
                </Box>

                <Box sx={simulatorCurrencyWrapperStyle}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label={translate('approvalSimulator.currency')}
                      select
                      value={values.currency?.isoCode || defaultCurrencyIsoCode}
                      onChange={(e) => {
                        onChange();
                        const selected = currencies?.find(
                          (c) => c.isoCode === e.target.value
                        );
                        setFieldValue('currency', selected || defaultCurrency);
                      }}
                      size="small"
                    >
                      {currencies?.map((currency) => (
                        <MenuItem key={currency.id} value={currency.isoCode}>
                          {currency.isoCode}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Box>
              </Box>

              {/* TODO: Uncomment when notValidatedCounterparty feature is fully implemented
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Switch
                  checked={values.notValidatedCounterparty || false}
                  onChange={(e) => {
                    onChange();
                    setFieldValue('notValidatedCounterparty', e.target.checked);
                  }}
                />
                <Typography sx={simulatorLabelStyle}>
                  {values.notValidatedCounterparty
                    ? translate('approvalSimulator.validatedCounterparty')
                    : translate('approvalSimulator.notValidatedCounterparty')}
                </Typography>
              </Box> */}

              <Button
                color="primary"
                startIcon={<PlayArrow />}
                sx={simulatorSimulateButtonStyle}
                onClick={() => onSubmit(values)}
                disabled={!isFormComplete}
              >
                {translate('approvalSimulator.simulate')}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};
